<script lang="ts">
  import { TaxTypes } from "../enums";
  import { tableLabels, taxLabels } from "../lang";
  import type { Dataset, Datasets, State } from "../types";
  import { getIncrease, getIncreaseNote } from "../utils/increase";
  import { numberToEuro, numberToPercent } from "../utils/number";
  import Tooltip from "./Tooltip.svelte";

  export let years: number[];
  export let region: Dataset;
  export let state: State;
  export let total: Datasets;
  export let tax: TaxTypes;

  $: showIncrease = years.length > 1;

  $: increaseRegion = getIncrease({ data: region?.tax, years, tax });
  $: increaseState = getIncrease({ data: state.data.tax, years, tax });
  $: increaseTotal = getIncrease({ data: total.data.tax, years, tax });

  $: increaseLabel = tableLabels.increaseLabel
    .replace("{start}", String(years.at(0)))
    .replace("{end}", String(years.at(-1)));
  $: increaseNote = getIncreaseNote({
    increase: increaseRegion,
    increaseStateAverage: getIncrease({ data: state.average, years, tax }),
    increaseTotalAverage: getIncrease({ data: total.average, years, tax }),
  });

  const numberFormatPerTax = {
    [TaxTypes.TAX]: numberToEuro,
    [TaxTypes.TAX_PER_CAPITA]: numberToEuro,
    [TaxTypes.RATE]: (number: number) => String(number),
  } as const;

  function formatNumber(number: number): string {
    return number === null ? tableLabels.na : numberFormatPerTax[tax](number);
  }
</script>

<div class="Table">
  <table>
    <thead>
      <tr>
        <th>{taxLabels[tax]}</th>
        {#each years as year}
          <th>{year}</th>
        {/each}
        {#if showIncrease}
          <th>{increaseLabel}</th>
        {/if}
      </tr>
    </thead>
    <tbody>
      {#if region}
        <tr>
          <th>{region.name}</th>
          {#each years as year}
            <td>{formatNumber(region.tax.get(year)[tax])}</td>
          {/each}
          {#if showIncrease}
            <td>
              <span class="Table-increase">
                <Tooltip label={tableLabels.increaseNote} text={increaseNote} />
                {numberToPercent(increaseRegion)}
              </span>
            </td>
          {/if}
        </tr>
      {/if}

      <tr>
        <th>{state.data.name}</th>
        {#each years as year}
          <td>{formatNumber(state.data.tax.get(year)[tax])}</td>
        {/each}
        {#if showIncrease}
          <td>{numberToPercent(increaseState)}</td>
        {/if}
      </tr>

      <tr>
        <th>{tableLabels.country}</th>
        {#each years as year}
          <td>{formatNumber(total.data.tax.get(year)[tax])}</td>
        {/each}
        {#if showIncrease}
          <td>{numberToPercent(increaseTotal)}</td>
        {/if}
      </tr>
    </tbody>
  </table>
</div>

<style>
  .Table {
    overflow-x: auto;
  }

  .Table-increase {
    align-items: center;
    display: inline-flex;
    gap: var(--size-8);
  }

  table {
    border-spacing: 0;
    color: var(--color-blue);
    font-size: var(--size-13);
    inline-size: 100%;
  }

  tr > :first-child {
    inset-inline-start: 0;
    position: sticky;
    text-align: start;
  }

  tr > :not(:first-child) {
    text-align: end;
  }

  tr > :where(:first-child, :last-child) {
    inline-size: 10rem;
    min-inline-size: 10rem;
  }

  th,
  td {
    background: var(--color-gray-background);
    border: var(--size-1) none var(--color-gray-border);
    padding: var(--size-16);
  }

  thead th:first-child {
    background: var(--color-blue);
    color: var(--color-white);
    font-family: var(--font-family-decoration);
    font-size: var(--size-15);
  }

  /* Border */

  th,
  td {
    border-block-start-style: solid;
    border-inline-end-style: solid;
  }

  :is(thead, tbody) tr > :first-child {
    border-inline-start-style: solid;
  }

  tbody tr:last-child > * {
    border-block-end-style: solid;
  }

  /* Border radius */

  thead tr > :first-child {
    border-start-start-radius: var(--size-4);
  }

  thead tr > :last-child {
    border-start-end-radius: var(--size-4);
  }

  tbody tr:last-child > :first-child {
    border-end-start-radius: var(--size-4);
  }

  tbody tr:last-child > :last-child {
    border-end-end-radius: var(--size-4);
  }
</style>
